import { useState } from "react";
import { X, Calendar, Clock, User, Plus, Trash2, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import DateCalendar from "../../DateCalendar";

interface Treatment {
  id: number;
  name: string;
  price: number;
}

interface BookAppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: {
    name: string;
    email: string;
    avatar?: string | null;
  };
  onSave?: (data: any) => void;
}

const availableTreatments = [
  { id: 1, name: "Dermal Filler", price: 350 },
  { id: 2, name: "Botox", price: 250 },
  { id: 3, name: "Lip Filler", price: 300 },
  { id: 4, name: "Chemical Peel", price: 150 },
  { id: 5, name: "Microneedling", price: 200 },
];

const providers = [
  { id: 1, name: "Emily S." },
  { id: 2, name: "Dr. Harper" },
  { id: 3, name: "Sarah M." },
];

const durations = [
  { value: "30", label: "30 minutes" },
  { value: "60", label: "1 hour" },
  { value: "90", label: "1.5 hours" },
  { value: "120", label: "2 hours" },
];

const reminders = [
  { value: "none", label: "No reminder" },
  { value: "1hour", label: "1 Hour Before" },
  { value: "1day", label: "1 Day Before" },
  { value: "2days", label: "2 Days Before" },
  { value: "1week", label: "1 Week Before" },
];

export default function BookAppointmentModal({
  isOpen,
  onClose,
  client,
  onSave,
}: BookAppointmentModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState("11:00");
  const [duration, setDuration] = useState("60");
  const [selectedTreatments, setSelectedTreatments] = useState<Treatment[]>([
    { id: 1, name: "Dermal Filler", price: 350 },
  ]);
  const [provider, setProvider] = useState("1");
  const [notes, setNotes] = useState("");
  const [reminder, setReminder] = useState("1day");
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isAddingTreatment, setIsAddingTreatment] = useState(false);

  const totalPrice = selectedTreatments.reduce((sum, t) => sum + t.price, 0);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const addTreatment = (treatment: typeof availableTreatments[0]) => {
    if (!selectedTreatments.find((t) => t.id === treatment.id)) {
      setSelectedTreatments([...selectedTreatments, treatment]);
    }
    setIsAddingTreatment(false);
  };

  const removeTreatment = (id: number) => {
    setSelectedTreatments(selectedTreatments.filter((t) => t.id !== id));
  };

  const handleSave = () => {
    const appointmentData = {
      clientName: client.name,
      date: selectedDate,
      time: selectedTime,
      duration,
      treatments: selectedTreatments,
      provider,
      notes,
      reminder,
      totalPrice,
    };
    onSave?.(appointmentData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#2d2d2d]">Add Appointment</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Client Info */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {client.avatar ? (
                <img src={client.avatar} alt={client.name} className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2d2d2d]">{client.name}</p>
              <p className="text-xs text-gray-500">{client.email}</p>
            </div>
          </div>

          {/* Patient / Date & Time */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Patient</label>
            <div className="flex gap-2">
              {/* Date Picker */}
              <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
                <PopoverTrigger asChild>
                  <button className="flex-1 flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] hover:border-purple-300 transition-colors">
                    <Calendar className="w-4 h-4 text-[#764BB7]" />
                    <span className="truncate">{formatDate(selectedDate)}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[1200] bg-white border border-gray-200 shadow-xl rounded-xl">
                  <DateCalendar
                    selectedDate={selectedDate}
                    onSelectDate={(date) => {
                      setSelectedDate(date);
                      setIsDateOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>

              {/* Time Input */}
              <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg">
                <Clock className="w-4 h-4 text-[#764BB7]" />
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="text-sm text-[#3d3d3d] outline-none w-20"
                />
              </div>

              {/* Duration */}
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="w-[130px] border-gray-200 h-[42px] text-sm">
                  <SelectValue placeholder="Duration" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1200]">
                  {durations.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      Duration: {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Treatments */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Treatments</label>
            
            {/* Add Treatment Buttons */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setIsAddingTreatment(!isAddingTreatment)}
                className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-purple-300 rounded-lg text-sm text-[#764BB7] hover:bg-purple-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Treatment
              </button>
              <button
                onClick={() => setIsAddingTreatment(!isAddingTreatment)}
                className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-purple-300 rounded-lg text-sm text-[#764BB7] hover:bg-purple-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Treatment
              </button>
            </div>

            {/* Treatment Dropdown */}
            {isAddingTreatment && (
              <div className="mb-3 p-2 border border-gray-200 rounded-lg bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Select a treatment:</p>
                <div className="space-y-1">
                  {availableTreatments
                    .filter((t) => !selectedTreatments.find((st) => st.id === t.id))
                    .map((treatment) => (
                      <button
                        key={treatment.id}
                        onClick={() => addTreatment(treatment)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-purple-50 rounded-lg text-sm text-[#3d3d3d] transition-colors"
                      >
                        <span>{treatment.name}</span>
                        <span className="text-[#764BB7] font-medium">${treatment.price.toFixed(2)}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Selected Treatments */}
            <div className="space-y-2">
              {selectedTreatments.map((treatment) => (
                <div
                  key={treatment.id}
                  className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center">
                      <Calendar className="w-3.5 h-3.5 text-[#764BB7]" />
                    </div>
                    <span className="text-sm text-[#3d3d3d] font-medium">{treatment.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#3d3d3d]">${treatment.price.toFixed(2)}</span>
                    <button
                      onClick={() => removeTreatment(treatment.id)}
                      className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Provider */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Provider</label>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger className="w-full border-gray-200 h-[42px] text-sm">
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1200]">
                {providers.map((p) => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any appointment notes here..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>

          {/* Reminder */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">Reminder</label>
            <Select value={reminder} onValueChange={setReminder}>
              <SelectTrigger className="w-full border-gray-200 h-[42px] text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <SelectValue placeholder="Select reminder" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1200]">
                {reminders.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-between">
            <div className="text-right">
              <span className="text-sm text-gray-500">Total: </span>
              <span className="text-xl font-bold text-[#764BB7]">${totalPrice.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-[#764BB7] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors"
              >
                Save Appointment
                <Calendar className="w-4 h-4" />
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

